/**
 * 챗 윈도우 — 메인 챗봇 UI
 *
 * 구성:
 * - Header: 봇 아바타, 이름, 닫기 버튼
 * - Body: 메시지 목록
 * - QuickReplies: 자주 찾는 질문
 * - Input: 메시지 입력창
 *
 * 자세한 디자인은 docs/02-design-system.md
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { Bubble } from '@/components/ui/Bubble';
import { TypingIndicator } from '@/components/ui/TypingIndicator';
import { Logo } from '@/components/ui/Logo';
import { NavBar } from './NavBar';
import { ContactOptions } from '@/components/messages/ContactOptions';
import { FAQMenu } from '@/components/messages/FAQMenu';
import { FAQSubmenu as FAQSubmenuComponent } from '@/components/messages/FAQSubmenu';
import { ServiceMenu } from '@/components/messages/ServiceMenu';
import { ServiceCategoryDetail } from '@/components/messages/ServiceCategoryDetail';
import { HomeCTA } from '@/components/messages/HomeCTA';
import { QuoteForm } from '@/components/messages/QuoteForm';
import { QuoteSuccess } from '@/components/messages/QuoteSuccess';
import { AgencyForm } from '@/components/messages/AgencyForm';
import { AgencySuccess } from '@/components/messages/AgencySuccess';
import { PrivateQuoteForm } from '@/components/messages/PrivateQuoteForm';
import { PrivateQuoteSuccess } from '@/components/messages/PrivateQuoteSuccess';
import { BalanceCard } from '@/components/messages/BalanceCard';
import { AuthHomeCTA } from '@/components/messages/AuthHomeCTA';
import { FeedbackForm } from '@/components/messages/FeedbackForm';
import { FeedbackSuccess } from '@/components/messages/FeedbackSuccess';
import {
  submitQuoteRequest,
  submitAgencyRequest,
  submitPrivateQuoteRequest,
  submitFeedback,
} from '@/lib/api/endpoints';
import faqMenuData from '@/data/faq-menu.json';
import serviceMenuAuthData from '@/data/service-menu-authenticated.json';
import serviceMenuAnonData from '@/data/service-menu-anonymous.json';
import type {
  AgencyFormData,
  CTAAction,
  CTAButton,
  FAQMenuData,
  FeedbackFormData,
  PrivateQuoteFormData,
  QuoteFormData,
  QuoteStep,
  ServiceItem,
  ServiceMenuData,
} from '@/types/qa';

const FAQ_DATA = faqMenuData as FAQMenuData;
const SERVICE_DATA_AUTH = serviceMenuAuthData as ServiceMenuData;
const SERVICE_DATA_ANON = serviceMenuAnonData as ServiceMenuData;

const ANONYMOUS_HOME_CTAS: CTAButton[] = [
  { id: 'features', label: '주요서비스 안내', action: 'show_features' },
  { id: 'member_benefits', label: '신규회원 특혜', action: 'show_intent', intent: 'new_member_benefits', highlight: true },
  { id: 'quote', label: '발송견적 요청', action: 'show_quote_form' },
  { id: 'managed_sending', label: '발송대행 요청', action: 'show_agency_form' },
  { id: 'api', label: '메시징 API', action: 'navigate_api' },
];

import { formatTime } from '@/lib/utils';
import type { ChatMessage } from '@/types/qa';

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (text: string) => void;
  onSelectByIntent: (intent: string, label: string) => void;
}

export function ChatWindow({
  isOpen,
  onClose,
  onSend,
  onSelectByIntent,
}: ChatWindowProps) {
  const messages = useChatStore((s) => s.messages);
  const isTyping = useChatStore((s) => s.isTyping);
  const reset = useChatStore((s) => s.reset);
  const addMessage = useChatStore((s) => s.addMessage);
  const authStatus = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');

  // 메시지 추가 시 자동 스크롤
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // 윈도우 열림 시 입력창 포커스
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
      }, 350);
      return () => {
        clearTimeout(t);
      };
    }
    return undefined;
  }, [isOpen]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
  }

  function handleHome() {
    reset();
  }

  function handleShowFAQ() {
    const status = useAuthStore.getState().status;
    const menuKey = status === 'authenticated' ? 'authenticated' : 'anonymous';
    const categories = FAQ_DATA.menus[menuKey];

    addMessage({
      role: 'bot',
      type: 'faq_menu',
      categories,
    });
  }

  function handleSelectSubmenu(submenuId: string) {
    const submenu = FAQ_DATA.submenus[submenuId];
    if (!submenu) return;
    const status = useAuthStore.getState().status;
    const parent = status === 'authenticated' ? 'authenticated' : 'anonymous';
    addMessage({
      role: 'bot',
      type: 'faq_submenu',
      submenu,
      parent,
    });
  }

  function handleBackToMainMenu() {
    handleShowFAQ();
  }

  // text 메시지에 인라인으로 붙는 ctaButtons 용도 (action만 전달됨).
  // HomeCTA 바는 별도 인라인 핸들러로 처리하여 show_intent의 intent까지 dispatch한다.
  function handleCTAAction(action: CTAAction) {
    switch (action) {
      case 'show_features':
        handleShowServices();
        break;
      case 'show_quote_form':
        handleStartQuoteForm();
        break;
      case 'show_agency_form':
        handleStartAgencyForm();
        break;
      case 'show_private_quote_form':
        handleStartPrivateQuoteForm();
        break;
      case 'show_intent':
        // 인라인 컨텍스트에서는 intent를 알 수 없으므로 no-op.
        break;
      case 'navigate_signup':
        // 회원가입 페이지로 새 탭 이동 (챗봇 세션은 유지).
        window.open('/intro/join', '_blank', 'noopener,noreferrer');
        break;
      case 'navigate_api':
        window.open(
          'https://www.maillink.co.kr/app/openapi/rest_intro.jsp',
          '_blank',
          'noopener,noreferrer'
        );
        break;
      case 'navigate_agency':
        window.open(
          'https://www.maillink.co.kr/intro/agencyintro',
          '_blank',
          'noopener,noreferrer'
        );
        break;
    }
  }

  function handleStartQuoteForm() {
    addMessage({
      role: 'bot',
      type: 'quote_form',
      step: 1,
      formData: {},
    });
  }

  function handleUpdateQuoteStep(
    nextStep: QuoteStep,
    data: Partial<QuoteFormData>
  ) {
    addMessage({
      role: 'bot',
      type: 'quote_form',
      step: nextStep,
      formData: data,
    });
  }

  async function handleSubmitQuote(data: QuoteFormData) {
    const sessionId = useChatStore.getState().sessionId;
    const context = useAuthStore.getState().context;

    const res = await submitQuoteRequest({
      ...data,
      consent_at: new Date().toISOString(),
      session_id: sessionId,
      source_page: context?.current_page ?? '/',
    });

    if (res.success) {
      addMessage({
        role: 'bot',
        type: 'quote_success',
        quoteId: res.data.quote_id,
        tier: res.data.tier,
      });
    } else {
      // 서버 응답 실패: tier는 제출된 volume으로 클라이언트에서 추정해 안내만 노출하지 않고,
      // 사용자가 다시 시도할 수 있도록 텍스트 메시지로 안내.
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '견적 요청 제출 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주시거나, 1:1 온라인 상담으로 문의해 주세요.',
      });
    }
  }

  function handleCancelQuote() {
    addMessage({
      role: 'bot',
      type: 'text',
      content:
        '견적 요청을 취소했습니다.\n\n언제든 다시 시작하실 수 있습니다. 다른 문의가 있으시면 말씀해 주세요.',
    });
  }

  function handleStartAgencyForm() {
    addMessage({
      role: 'bot',
      type: 'agency_form',
      formData: {},
    });
  }

  async function handleSubmitAgency(data: AgencyFormData) {
    const sessionId = useChatStore.getState().sessionId;
    const context = useAuthStore.getState().context;

    const res = await submitAgencyRequest({
      ...data,
      consent_at: new Date().toISOString(),
      session_id: sessionId,
      source_page: context?.current_page ?? '/',
    });

    if (res.success) {
      addMessage({
        role: 'bot',
        type: 'agency_success',
        requestId: res.data.request_id,
      });
    } else {
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '발송대행 신청 제출 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주시거나, 1:1 온라인 상담으로 문의해 주세요.',
      });
    }
  }

  function handleCancelAgency() {
    addMessage({
      role: 'bot',
      type: 'text',
      content:
        '발송대행 신청을 취소했습니다.\n\n언제든 다시 시작하실 수 있습니다. 다른 문의가 있으시면 말씀해 주세요.',
    });
  }

  function handleStartPrivateQuoteForm() {
    addMessage({
      role: 'bot',
      type: 'private_quote_form',
      formData: {},
    });
  }

  async function handleSubmitPrivateQuote(data: PrivateQuoteFormData) {
    const sessionId = useChatStore.getState().sessionId;
    const context = useAuthStore.getState().context;

    const res = await submitPrivateQuoteRequest({
      ...data,
      consent_at: new Date().toISOString(),
      session_id: sessionId,
      source_page: context?.current_page ?? '/',
    });

    if (res.success) {
      addMessage({
        role: 'bot',
        type: 'private_quote_success',
        requestId: res.data.request_id,
      });
    } else {
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '프라이빗 견적 요청 제출 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주시거나, 1:1 온라인 상담으로 문의해 주세요.',
      });
    }
  }

  function handleCancelPrivateQuote() {
    addMessage({
      role: 'bot',
      type: 'text',
      content:
        '프라이빗 견적 요청을 취소했습니다.\n\n언제든 다시 시작하실 수 있습니다. 다른 문의가 있으시면 말씀해 주세요.',
    });
  }

  // ── 도움말 / 피드백 (로그인 사용자 전용) ──

  function handleShowHelp() {
    const url = useAuthStore.getState().context?.help_url;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleStartFeedback() {
    addMessage({
      role: 'bot',
      type: 'feedback_form',
      formData: {},
    });
  }

  async function handleSubmitFeedback(data: FeedbackFormData) {
    const sessionId = useChatStore.getState().sessionId;
    const context = useAuthStore.getState().context;

    const res = await submitFeedback({
      message: data.message,
      page: context?.current_page ?? '/',
      session_id: sessionId,
    });

    if (res.success) {
      addMessage({
        role: 'bot',
        type: 'feedback_success',
        ticketId: res.data.ticket_id,
      });
    } else if (res.code === 'AUTH_REQUIRED') {
      addMessage({
        role: 'bot',
        type: 'login_prompt',
        content: res.message || '로그인 후 다시 시도해 주세요.',
        loginUrl: res.meta.login_url ?? '/login',
      });
    } else {
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '신고 제출 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주시거나, 1:1 온라인 상담으로 문의해 주세요.',
      });
    }
  }

  function handleCancelFeedback() {
    addMessage({
      role: 'bot',
      type: 'text',
      content:
        '신고를 취소했습니다.\n\n다른 문의가 있으시면 말씀해 주세요.',
    });
  }

  function handleShowServices() {
    const status = useAuthStore.getState().status;
    const isAuth = status === 'authenticated';
    const data = isAuth ? SERVICE_DATA_AUTH : SERVICE_DATA_ANON;

    addMessage({
      role: 'bot',
      type: 'service_menu',
      categories: data.menu,
      isAuthenticated: isAuth,
    });
  }

  function handleSelectServiceCategory(categoryId: string) {
    const status = useAuthStore.getState().status;
    const data = status === 'authenticated' ? SERVICE_DATA_AUTH : SERVICE_DATA_ANON;
    const category = data.menu.find((c) => c.id === categoryId);
    if (!category) return;
    addMessage({
      role: 'bot',
      type: 'service_category_detail',
      category,
    });
  }

  function handleBackToServices() {
    handleShowServices();
  }

  function handleSelectLockedItem(item: ServiceItem) {
    addMessage({
      role: 'bot',
      type: 'login_prompt',
      content: `"${item.label}" 기능은 로그인 후 이용 가능합니다.\n\n로그인하시면 즉시 확인하실 수 있습니다.`,
      loginUrl: '/login',
    });
  }

  function handleContactAgent() {
    const config = useAuthStore.getState().config;
    const isOperating = config?.is_operating_now ?? false;
    const supportPhone = config?.support_phone ?? '1544-9231';

    addMessage({
      role: 'bot',
      type: 'contact_options',
      isOperating,
      supportPhone,
      onlineContactUrl: '/app/counsel.jsp',
    });
  }

  const greeting =
    authStatus === 'authenticated' && user
      ? `${user.name}님, 안녕하세요! 무엇을 도와드릴까요?`
      : '안녕하세요! 메일링크에 대해 무엇이든 물어보세요.';

  return (
    <div
      role="dialog"
      aria-label="메일링크 고객지원 챗봇"
      aria-hidden={!isOpen}
      className={cn(
        'ml-fixed ml-right-7 ml-bottom-[100px] ml-z-cb-window',
        'ml-w-[380px] ml-h-[720px] ml-max-h-[calc(100vh-130px)]',
        'ml-bg-white ml-rounded-cb-xl ml-shadow-cb-window ml-border ml-border-line',
        'ml-flex ml-flex-col ml-overflow-hidden',
        'ml-origin-bottom-right ml-transition-all ml-duration-300',
        isOpen
          ? 'ml-opacity-100 ml-scale-100 ml-translate-y-0 ml-pointer-events-auto'
          : 'ml-opacity-0 ml-scale-90 ml-translate-y-4 ml-pointer-events-none',
        // 모바일 풀스크린
        'max-[480px]:ml-right-2.5 max-[480px]:ml-left-2.5 max-[480px]:ml-w-auto max-[480px]:ml-bottom-[90px] max-[480px]:ml-h-[calc(100vh-110px)]'
      )}
    >
      <Header onClose={onClose} greeting={greeting} />

      <div
        ref={bodyRef}
        className={cn(
          'ml-flex-1 ml-overflow-y-auto ml-px-4 ml-py-4',
          'ml-flex ml-flex-col ml-gap-2.5',
          'ml-bg-surface ml-chat-scroll'
        )}
      >
        {messages.map((msg) => (
          <MessageRenderer
            key={msg.id}
            message={msg}
            onSend={onSend}
            onSelectByIntent={onSelectByIntent}
            onSelectSubmenu={handleSelectSubmenu}
            onBackToMainMenu={handleBackToMainMenu}
            onSelectServiceCategory={handleSelectServiceCategory}
            onSelectLockedItem={handleSelectLockedItem}
            onTriggerQuoteForm={handleStartQuoteForm}
            onCTAAction={handleCTAAction}
            onBackToServices={handleBackToServices}
            onUpdateQuoteStep={handleUpdateQuoteStep}
            onSubmitQuote={handleSubmitQuote}
            onCancelQuote={handleCancelQuote}
            onSubmitAgency={handleSubmitAgency}
            onCancelAgency={handleCancelAgency}
            onSubmitPrivateQuote={handleSubmitPrivateQuote}
            onCancelPrivateQuote={handleCancelPrivateQuote}
            onSubmitFeedback={handleSubmitFeedback}
            onCancelFeedback={handleCancelFeedback}
          />
        ))}
        {isTyping && <TypingIndicator />}
      </div>

      {messages.length <= 1 &&
        (authStatus === 'authenticated' ? (
          <AuthHomeCTA
            helpUrl={useAuthStore.getState().context?.help_url}
            onShowHelp={handleShowHelp}
            onShowFeedback={handleStartFeedback}
          />
        ) : (
          <HomeCTA
            buttons={ANONYMOUS_HOME_CTAS}
            onAction={(btn) => {
              if (btn.action === 'show_intent' && btn.intent) {
                onSelectByIntent(btn.intent, btn.label);
              } else {
                handleCTAAction(btn.action);
              }
            }}
          />
        ))}

      <NavBar
        onHome={handleHome}
        onShowServices={handleShowServices}
        onContactAgent={handleContactAgent}
      />

      <form
        onSubmit={handleSubmit}
        className={cn(
          'ml-flex ml-gap-2 ml-items-end',
          'ml-px-3 ml-py-3 ml-bg-white ml-border-t ml-border-line'
        )}
      >
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="메시지를 입력하세요..."
          rows={1}
          aria-label="메시지 입력"
          className={cn(
            'ml-flex-1 ml-font-sans ml-text-cb-base',
            'ml-px-3 ml-py-2.5 ml-rounded-cb-md',
            'ml-border ml-border-line ml-bg-surface',
            'ml-resize-none ml-min-h-[40px] ml-max-h-[100px]',
            'focus:ml-outline-none focus:ml-border-brand-600 focus:ml-bg-white',
            'ml-text-ink ml-leading-snug'
          )}
        />
        <button
          type="submit"
          aria-label="전송"
          disabled={!inputValue.trim()}
          className={cn(
            'ml-w-10 ml-h-10 ml-rounded-cb-md',
            'ml-bg-brand-600 ml-text-white',
            'ml-flex ml-items-center ml-justify-center',
            'ml-cursor-pointer ml-transition-colors ml-duration-150 ml-flex-shrink-0',
            'hover:ml-bg-brand-500',
            'disabled:ml-bg-mute disabled:ml-cursor-not-allowed'
          )}
        >
          <SendIcon />
        </button>
      </form>
    </div>
  );
}

interface HeaderProps {
  onClose: () => void;
  greeting: string;
}

function Header({ onClose, greeting }: HeaderProps) {
  return (
    <div
      className={cn(
        'ml-relative ml-px-4 ml-pt-4 ml-pb-3.5 ml-flex-shrink-0',
        'ml-bg-gradient-to-br ml-from-brand-600 ml-to-brand-500 ml-text-white'
      )}
    >
      <div className="ml-relative ml-flex ml-justify-between ml-items-start">
        <div className="ml-flex ml-gap-2.5 ml-items-center">
          <div className="ml-relative">
            <Logo size="md" />
            <span
              className="ml-absolute ml--right-0.5 ml--bottom-0.5 ml-w-3 ml-h-3 ml-rounded-full ml-bg-[#5CE2A0] ml-border-2 ml-border-brand-600"
              aria-hidden="true"
            />
          </div>
          <div>
            <div className="ml-font-semibold ml-text-cb-lg ml-tracking-tight">
              메일링크 AI 도우미
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="챗봇 닫기"
          className={cn(
            'ml-w-7 ml-h-7 ml-rounded-cb-sm',
            'ml-bg-white/15 ml-text-white ml-cursor-pointer',
            'ml-flex ml-items-center ml-justify-center',
            'ml-transition-colors ml-duration-150',
            'hover:ml-bg-white/25'
          )}
        >
          <ChevronDownIcon />
        </button>
      </div>
      <div className="ml-relative ml-mt-3.5 ml-text-cb-base ml-leading-snug ml-opacity-95">
        {greeting}
      </div>
    </div>
  );
}

interface MessageRendererProps {
  message: ChatMessage;
  onSend: (text: string) => void;
  onSelectByIntent: (intent: string, label: string) => void;
  onSelectSubmenu: (submenuId: string) => void;
  onBackToMainMenu: () => void;
  onSelectServiceCategory: (categoryId: string) => void;
  onSelectLockedItem: (item: ServiceItem) => void;
  onTriggerQuoteForm: () => void;
  onCTAAction: (action: CTAAction) => void;
  onBackToServices: () => void;
  onUpdateQuoteStep: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
  onSubmitQuote: (data: QuoteFormData) => void;
  onCancelQuote: () => void;
  onSubmitAgency: (data: AgencyFormData) => void;
  onCancelAgency: () => void;
  onSubmitPrivateQuote: (data: PrivateQuoteFormData) => void;
  onCancelPrivateQuote: () => void;
  onSubmitFeedback: (data: FeedbackFormData) => void;
  onCancelFeedback: () => void;
}

function MessageRenderer({
  message,
  onSend: _onSend,
  onSelectByIntent,
  onSelectSubmenu,
  onBackToMainMenu,
  onSelectServiceCategory,
  onSelectLockedItem,
  onTriggerQuoteForm,
  onCTAAction,
  onBackToServices,
  onUpdateQuoteStep,
  onSubmitQuote,
  onCancelQuote,
  onSubmitAgency,
  onCancelAgency,
  onSubmitPrivateQuote,
  onCancelPrivateQuote,
  onSubmitFeedback,
  onCancelFeedback,
}: MessageRendererProps) {
  const time = formatTime(message.timestamp);

  switch (message.type) {
    case 'text':
      return (
        <Bubble role={message.role === 'user' ? 'user' : 'bot'} timestamp={time}>
          {message.content}
          {message.ctaButtons && message.ctaButtons.length > 0 && (
            <div className="ml-mt-3 ml-flex ml-flex-wrap ml-gap-2">
              {message.ctaButtons.map((cta) => (
                <button
                  key={cta.action}
                  type="button"
                  onClick={() => onCTAAction(cta.action)}
                  className={cn(
                    'ml-inline-flex ml-items-center ml-gap-1.5',
                    'ml-px-3 ml-py-2 ml-rounded-cb-sm',
                    'ml-bg-brand-600 ml-text-white ml-text-cb-sm ml-font-semibold',
                    'ml-cursor-pointer ml-transition-colors hover:ml-bg-brand-500'
                  )}
                >
                  {cta.label}
                  <span aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          )}
          {message.links && message.links.length > 0 && (
            <div className="ml-mt-2.5 ml-pt-2.5 ml-border-t ml-border-dashed ml-border-line ml-flex ml-flex-col ml-gap-1">
              {message.links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-text-brand-500 ml-text-cb-sm hover:ml-underline"
                >
                  ↗ {link.title}
                </a>
              ))}
            </div>
          )}
        </Bubble>
      );

    case 'contact_options':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-mb-1">
            상담원과 연결을 도와드리겠습니다. 어떤 방법이 편하신가요?
          </div>
          <ContactOptions
            isOperating={message.isOperating}
            supportPhone={message.supportPhone}
            onlineContactUrl={message.onlineContactUrl}
          />
        </Bubble>
      );

    case 'faq_menu':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-mb-1">자주 찾는 질문 중에서 선택해 주세요.</div>
          <FAQMenu
            categories={message.categories}
            onSelectIntent={onSelectByIntent}
            onSelectSubmenu={onSelectSubmenu}
          />
        </Bubble>
      );

    case 'faq_submenu':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-font-semibold ml-mb-1">{message.submenu.title}</div>
          <div className="ml-text-cb-sm ml-text-mute ml-mb-2">
            {message.submenu.description}
          </div>
          <FAQSubmenuComponent
            submenu={message.submenu}
            onSelectIntent={onSelectByIntent}
            onBack={onBackToMainMenu}
          />
        </Bubble>
      );

    case 'service_menu':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-mb-1 ml-font-semibold">
            {message.isAuthenticated
              ? '메일링크 서비스 메뉴입니다.'
              : '메일링크 서비스 안내입니다.'}
          </div>
          <div className="ml-text-cb-sm ml-text-mute ml-mb-2">
            {message.isAuthenticated
              ? '어떤 기능에 대해 알아보시겠어요?'
              : '관심 있는 서비스를 선택해 주세요.'}
          </div>
          <ServiceMenu
            categories={message.categories}
            onSelectCategory={onSelectServiceCategory}
          />
        </Bubble>
      );

    case 'service_category_detail':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-flex ml-items-center ml-gap-2 ml-font-semibold ml-mb-1">
            {message.category.icon && <span>{message.category.icon}</span>}
            <span>{message.category.label}</span>
          </div>
          <div className="ml-text-cb-sm ml-text-mute ml-mb-2">
            {message.category.description}
          </div>
          <ServiceCategoryDetail
            category={message.category}
            onSelectIntent={onSelectByIntent}
            onSelectLockedItem={onSelectLockedItem}
            onTriggerQuoteForm={onTriggerQuoteForm}
            onBack={onBackToServices}
          />
        </Bubble>
      );

    case 'quote_form':
      return (
        <Bubble role="bot" timestamp={time}>
          <QuoteForm
            step={message.step}
            formData={message.formData}
            onUpdate={onUpdateQuoteStep}
            onSubmit={onSubmitQuote}
            onCancel={onCancelQuote}
          />
        </Bubble>
      );

    case 'quote_success':
      return (
        <Bubble role="bot" timestamp={time}>
          <QuoteSuccess quoteId={message.quoteId} tier={message.tier} />
        </Bubble>
      );

    case 'agency_form':
      return (
        <Bubble role="bot" timestamp={time}>
          <AgencyForm
            formData={message.formData}
            onSubmit={onSubmitAgency}
            onCancel={onCancelAgency}
          />
        </Bubble>
      );

    case 'agency_success':
      return (
        <Bubble role="bot" timestamp={time}>
          <AgencySuccess requestId={message.requestId} />
        </Bubble>
      );

    case 'private_quote_form':
      return (
        <Bubble role="bot" timestamp={time}>
          <PrivateQuoteForm
            formData={message.formData}
            onSubmit={onSubmitPrivateQuote}
            onCancel={onCancelPrivateQuote}
          />
        </Bubble>
      );

    case 'private_quote_success':
      return (
        <Bubble role="bot" timestamp={time}>
          <PrivateQuoteSuccess requestId={message.requestId} />
        </Bubble>
      );

    case 'feedback_form':
      return (
        <Bubble role="bot" timestamp={time}>
          <FeedbackForm
            formData={message.formData}
            onSubmit={onSubmitFeedback}
            onCancel={onCancelFeedback}
          />
        </Bubble>
      );

    case 'feedback_success':
      return (
        <Bubble role="bot" timestamp={time}>
          <FeedbackSuccess ticketId={message.ticketId} />
        </Bubble>
      );

    case 'login_prompt':
      return (
        <Bubble role="bot" timestamp={time}>
          <div className="ml-mb-3">{message.content}</div>
          <div className="ml-flex ml-gap-2">
            <a
              href={message.loginUrl ?? '/login'}
              className={cn(
                'ml-inline-block ml-px-3 ml-py-1.5 ml-rounded-cb-sm',
                'ml-bg-brand-600 ml-text-white ml-text-cb-sm ml-font-medium',
                'hover:ml-bg-brand-500 ml-no-underline'
              )}
            >
              로그인하기
            </a>
            <a
              href="/signup"
              className={cn(
                'ml-inline-block ml-px-3 ml-py-1.5 ml-rounded-cb-sm',
                'ml-bg-white ml-text-brand-600 ml-text-cb-sm ml-font-medium',
                'ml-border ml-border-brand-600 ml-no-underline'
              )}
            >
              회원가입
            </a>
          </div>
        </Bubble>
      );

    case 'balance_card':
      return (
        <Bubble role="bot" timestamp={time}>
          <BalanceCard data={message.data} />
        </Bubble>
      );

    // 별도 슬롯에서 렌더되거나 (typing → TypingIndicator, home_cta → HomeCTA 슬롯)
    // 추가 구현 예정인 variant들. 명시적으로 null을 반환해 union을 모두 소비한다.
    case 'typing':
    case 'home_cta':
    case 'campaign_list':
    case 'alert':
    case 'escalation':
    case 'quick_replies':
      return null;

    default:
      // exhaustive check — 새 ChatMessage variant 추가 시 컴파일 에러로 알림
      message satisfies never;
      return null;
  }
}

function ChevronDownIcon() {
  return (
    <svg
      className="ml-w-3.5 ml-h-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      className="ml-w-4 ml-h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}
